import type { LucideIcon } from 'lucide-react';
import {
	Building2,
	CalendarDays,
	FileText,
	Gem,
	Headphones,
	Hospital,
	MessageCircleQuestionMark,
	Newspaper,
	Package,
	Shield,
	Stethoscope,
	Users,
} from 'lucide-react';

export type AdminDashboardCardSize = 'large' | 'medium' | 'compact';

export type AdminDashboardCardStats = {
	primary: string;
	secondary: string[];
};

export type AdminDashboardCardConfig = {
	key: string;
	title: string;
	path: string;
	icon: LucideIcon;
	getStats?: (data: Dashboard.Indicators) => AdminDashboardCardStats | null;
};

export type AdminDashboardRowItem =
	| { type: 'card'; key: string }
	| { type: 'group'; key: string; keys: string[] };

export type AdminDashboardRow = {
	size: AdminDashboardCardSize;
	columns: 2 | 3;
	items: AdminDashboardRowItem[];
};

const adminDashboardCardsMap: Record<string, AdminDashboardCardConfig> = {
	sessions: {
		key: 'sessions',
		title: 'Consultas',
		path: '/admin/consultas',
		icon: CalendarDays,
		getStats: (data) => ({
			primary: String(data.sessions.total),
			secondary: [
				`${data.sessions.consultations} consultas`,
				`${data.sessions.returns} retornos`,
				`${data.sessions.cancelled} canceladas`,
				`${data.sessions.paid} pagas`,
			],
		}),
	},
	professionals: {
		key: 'professionals',
		title: 'Profissionais',
		path: '/admin/profissionais',
		icon: Stethoscope,
		getStats: (data) => ({
			primary: String(data.professionals.total),
			secondary: [`${data.professionals.inactive} desativados`],
		}),
	},
	users: {
		key: 'users',
		title: 'Usuários',
		path: '/admin/pacientes',
		icon: Users,
		getStats: (data) => ({
			primary: String(data.users.total),
			secondary: [`${data.users.inactive} desativados`],
		}),
	},
	services: {
		key: 'services',
		title: 'Serviços',
		path: '/admin/recursos',
		icon: Package,
		getStats: (data) => ({
			primary: String(data.services.total),
			secondary: [`${data.services.inactive} inativos`],
		}),
	},
	clubs: {
		key: 'clubs',
		title: 'Clubes',
		path: '/admin/clubes',
		icon: Gem,
		getStats: (data) => ({
			primary: String(data.clubs.total),
			secondary: [`${data.clubs.inactive} inativos`],
		}),
	},
	forms: {
		key: 'forms',
		title: 'Formulários',
		path: '/admin/formularios',
		icon: FileText,
		getStats: (data) => ({
			primary: String(data.forms.total),
			secondary: [`${data.forms.inactive} inativos`],
		}),
	},
	companies: {
		key: 'companies',
		title: 'Empresas',
		path: '/admin/empresas',
		icon: Building2,
		getStats: (data) => ({
			primary: String(data.companies.total),
			secondary: [`${data.companies.inactive} inativas`],
		}),
	},
	clinics: {
		key: 'clinics',
		title: 'Clínicas',
		path: '/admin/clinicas',
		icon: Hospital,
		getStats: (data) => ({
			primary: String(data.clinics.total),
			secondary: [`${data.clinics.inactive} desativadas`],
		}),
	},
	admins: {
		key: 'admins',
		title: 'Admin',
		path: '/admin/admins',
		icon: Shield,
		getStats: (data) => ({
			primary: String(data.admins.total),
			secondary: [],
		}),
	},
	support: {
		key: 'support',
		title: 'Suporte',
		path: '/admin/suporte',
		icon: Headphones,
		getStats: (data) => ({
			primary: String(data.support.total),
			secondary: [],
		}),
	},
	articles: {
		key: 'articles',
		title: 'Artigos',
		path: '/admin/blog',
		icon: Newspaper,
		getStats: (data) => ({
			primary: String(data.blog.total),
			secondary: [`${data.blog.views} acessos`],
		}),
	},
	qa: {
		key: 'qa',
		title: 'Q&A',
		path: '/admin/faq',
		icon: MessageCircleQuestionMark,
		getStats: (data) => ({
			primary: String(data.questions.total),
			secondary: [`${data.questions.views} acessos`],
		}),
	},
};

export const adminDashboardRows: AdminDashboardRow[] = [
	{
		size: 'large',
		columns: 3,
		items: [
			{ type: 'card', key: 'sessions' },
			{ type: 'card', key: 'professionals' },
			{ type: 'card', key: 'users' },
		],
	},
	{
		size: 'medium',
		columns: 3,
		items: [
			{ type: 'card', key: 'services' },
			{ type: 'card', key: 'clubs' },
			{ type: 'card', key: 'forms' },
		],
	},
	{
		size: 'compact',
		columns: 3,
		items: [
			{
				type: 'group',
				key: 'organizations',
				keys: ['companies', 'clinics'],
			},
			{ type: 'group', key: 'access', keys: ['admins', 'support'] },
			{ type: 'group', key: 'content', keys: ['articles', 'qa'] },
		],
	},
];

export const getAdminDashboardCard = (
	key: string,
): AdminDashboardCardConfig | undefined => adminDashboardCardsMap[key];

export const getAllAdminDashboardKeys = (): string[] =>
	adminDashboardRows.flatMap((row) =>
		row.items.flatMap((item) =>
			item.type === 'group' ? item.keys : [item.key],
		),
	);
